import { useWorkspaceAccrualAccounts } from "@api/account.api";
import { useScheduleTemplate } from "@api/scheduleTemplate.api";
import { currencyFormatter } from "@common/helpers/currency";
import { resolveBillRecognitionAccountId } from "@common/helpers/domains/accounting/scheduleBillRecognitionAccount";
import {
    ScheduleExpandableModal,
    ScheduleModalLoadError,
} from "./ScheduleExpandableModal";
import { ScheduleManualLayout } from "./scheduleManualLayout";
import { ScheduleManualBody } from "./ScheduleManualBody";
import { ScheduleManualModalFrame } from "./ScheduleManualModalFrame";
import { ScheduleManualPreviewPane } from "./ScheduleManualPreviewPane";
import { ScheduleAccruedTermField } from "./ScheduleAccruedTermField";
import { followAccruedDraftTermStart } from "./scheduleAccruedDraftStart";
import {
    isAccruedBilledSource,
    summarizeAccruedTerm,
} from "./scheduleAccruedTerm";
import { isAwaitingSpecificDate, ScheduleDraft } from "./scheduleDraft";
import {
    getSpecificDateDefaultTermStart,
    resolveSpecificDateApply,
    ScheduleSpecificDateApplyRequest,
    ScheduleSpecificDateApplyResult,
} from "./scheduleSpecificDateApply";
import { useScheduleApplyDraft } from "./useScheduleApplyDraft";
import { isTermRecognitionMethod } from "@common/types/domains/accounting/schedule";

const TITLE = "Apply schedule";
const SPECIFIC_DATE_SUBTITLE =
    "Pick the date this schedule recognizes on; expand to full screen to preview it";
const REVIEW_SUBTITLE =
    "Review when this schedule recognizes; expand to full screen to preview it";

interface ScheduleSpecificDateApplyModalProps {
    request: ScheduleSpecificDateApplyRequest | null;
    onHide: () => void;
    isSubmitting: boolean;
    onSubmit: (result: ScheduleSpecificDateApplyResult) => void;
}

/**
 * A Specific date template cannot resolve its date from the source, so apply
 * opens the schedule modal with that date empty and highlighted, and Apply
 * stays disabled until it is set. An accrued template opens it too, with its
 * back-dated start seeded, so the user sees and can move it before anything
 * posts. The modal stays open while the apply runs.
 */
export const ScheduleSpecificDateApplyModal = ({
    request,
    onHide,
    isSubmitting,
    onSubmit,
}: ScheduleSpecificDateApplyModalProps) => {
    const hide = () => {
        if (!isSubmitting) {
            onHide();
        }
    };
    return (
        <ScheduleExpandableModal item={request} onHide={hide}>
            {({ item, isExpanded, onExpandedChange }) => (
                <ScheduleSpecificDateApplyContent
                    request={item}
                    isExpanded={isExpanded}
                    onExpandedChange={onExpandedChange}
                    onHide={hide}
                    isSubmitting={isSubmitting}
                    onSubmit={onSubmit}
                />
            )}
        </ScheduleExpandableModal>
    );
};

const ScheduleSpecificDateApplyContent = ({
    request,
    isExpanded,
    onExpandedChange,
    onHide,
    isSubmitting,
    onSubmit,
}: Omit<ScheduleSpecificDateApplyModalProps, "request"> & {
    request: ScheduleSpecificDateApplyRequest;
    isExpanded: boolean;
    onExpandedChange: (isExpanded: boolean) => void;
}) => {
    const templateQuery = useScheduleTemplate(request.templateId);
    const { data: accrualAccounts } = useWorkspaceAccrualAccounts();
    const sourceRecognitionAccountId =
        templateQuery.data && request.sourceLineAccountIds
            ? resolveBillRecognitionAccountId({
                  scheduleType: templateQuery.data.type,
                  lineAccountIds: request.sourceLineAccountIds,
                  getAccountType: (accountId) =>
                      accrualAccounts?.find(
                          (account) => account.id === accountId,
                      )?.type,
              })
            : null;
    const seedTermStart = getSpecificDateDefaultTermStart({
        template: templateQuery.data,
        term: templateQuery.data ?? null,
        sourceDate: request.sourceDate,
    });
    const { draftState, preview, isApplicable, draftErrorByLayout } =
        useScheduleApplyDraft({
            template: templateQuery.data,
            entityId: request.entityId,
            amount: request.amount,
            termStart: seedTermStart,
            sourceDate: request.sourceDate,
            fixedAmount: request.amount,
            sourceRecognitionAccountId,
            billing: request.billing,
        });
    const layout: ScheduleManualLayout = isExpanded ? "drawer" : "modal";
    const defaultTermStart = getSpecificDateDefaultTermStart({
        template: templateQuery.data,
        term: draftState.draft,
        sourceDate: request.sourceDate,
    });
    if (templateQuery.isError) {
        return <ScheduleModalLoadError title={TITLE} onHide={onHide} />;
    }
    const result = resolveSpecificDateApply({
        resolved: draftState.resolved,
        overrides: draftState.overrides,
        defaultTermStart,
    });
    const canSubmit = result !== null && isApplicable;
    const subtitle = isAwaitingSpecificDate(draftState.draft)
        ? SPECIFIC_DATE_SUBTITLE
        : REVIEW_SUBTITLE;
    const onDraftChange = (next: ScheduleDraft) =>
        draftState.setDraft(
            draftState.draft
                ? followAccruedDraftTermStart({
                      subModes: templateQuery.data,
                      sourceType: request.sourceType,
                      sourceDate: request.sourceDate,
                      previous: draftState.draft,
                      next,
                  })
                : next,
        );

    const draft = draftState.draft;
    const isAccruedTermShown = isAccruedBilledSource({
        subModes: templateQuery.data,
        sourceType: request.sourceType,
    });
    const termMethod = isAccruedTermShown
        ? draft?.rows.find((row) => isTermRecognitionMethod(row.method))?.method
        : undefined;
    const isSingleComponent = draft?.rows.length === 1;
    const accruedTerm =
        draft && termMethod ? (
            <ScheduleAccruedTermField
                term={{
                    length: draft.termLength,
                    unit: draft.termUnit,
                }}
                onTermChange={({ length, unit }) =>
                    onDraftChange({
                        ...draft,
                        termLength: length,
                        termUnit: unit,
                    })
                }
                method={termMethod}
                summary={
                    preview.isSettled && isSingleComponent
                        ? summarizeAccruedTerm(preview.ladder)
                        : null
                }
                layout={layout}
            />
        ) : undefined;

    return (
        <ScheduleManualModalFrame
            title={TITLE}
            subtitle={`${request.templateName} · ${request.sourceLabel} · ${currencyFormatter.format(request.amount)} · ${subtitle}`}
            isExpanded={isExpanded}
            onExpandedChange={onExpandedChange}
            preview={<ScheduleManualPreviewPane preview={preview} />}
            secondaryLabel="Cancel"
            onSecondary={onHide}
            expandedSecondaryLabel="Back"
            submitLabel="Apply schedule"
            isSubmitDisabled={!canSubmit}
            isSubmitting={isSubmitting}
            onSubmit={() => {
                if (result && canSubmit) {
                    onSubmit(result);
                }
            }}
        >
            <ScheduleManualBody
                template={templateQuery.data}
                entityId={request.entityId}
                draft={draftState.draft}
                onDraftChange={onDraftChange}
                draftError={draftErrorByLayout[layout]}
                isEditable
                isSpecificDateEditable
                isHoldingAccountEditable
                layout={layout}
                details={accruedTerm}
            />
        </ScheduleManualModalFrame>
    );
};
